import {UserConfig} from "../../../../types/UserConfig";
import {
    EquityEtoPricingStrategy,
    EquityPricingStrategy,
    EquityTmcPricingStrategy,
    IndexPricingStrategy,
    PriceStrategy
} from "../input/PriceStrategy";
import {StringKeyed} from "../../../../types/GenericTypes";
import {Optional} from "../../../../js/util/Optional";
import {SecurityUtil} from "../../../../js/util/SecurityUtil";
import {AsxInstrumentUtil} from "../../../../js/util/asx/AsxInstrumentUtil";
import {Exchange} from "../../../../instruments/exchange/Exchange";

export class PriceStrategyService {

    public static id = "priceStrategyService";

    private cache: StringKeyed<PriceStrategy> = {};

    /** @ngInject */
    public constructor(private readonly userConfig: UserConfig) {

    }

    public forCode(code: string): PriceStrategy {
        return Optional.of(this.cache[code])
                       .orElseGet(() => {
                           const strategy = this.buildStrategy(code);
                           this.cache[code] = strategy;
                           return strategy;
                       });
    }

    private buildStrategy(code: string): PriceStrategy {
        if (this.looksLikeItsATmc(code)) {
            return this.forTmc(code);
        }

        try {
            const exchange = AsxInstrumentUtil.guessExchange(code);
            if (exchange === Exchange.ASX || exchange === Exchange.AXW) {
                return this.forUnderlying(code);
            } else {
                return this.forEquityEto(code);
            }
        } catch (e) {
            return this.forUnderlying(code);
        }
    }

    private forUnderlying(underlying: string): PriceStrategy {
        if (AsxInstrumentUtil.isIndex(underlying)) {
            return new IndexPricingStrategy();
        } else {
            return new EquityPricingStrategy(this.userConfig);
        }
    }

    private forEquityEto(code: string): PriceStrategy {
        const underlying = SecurityUtil.underlying(code);
        if (AsxInstrumentUtil.isIndex(underlying)) {
            return new IndexPricingStrategy();
        } else {
            return new EquityEtoPricingStrategy(this.userConfig);
        }
    }

    private forTmc(code: string): PriceStrategy {
        const underlying = SecurityUtil.underlying(code);
        if (AsxInstrumentUtil.isIndex(underlying)) {
            return new IndexPricingStrategy();
        } else {
            return new EquityTmcPricingStrategy(this.userConfig);
        }
    }

    private looksLikeItsATmc(code: string) {
        return /[A-Z0-9]{3}\d{3}/i.test(code);
    }
}
